/**
 * 单项目能源大屏只读接口层。
 *
 * 所有请求均按 managerId 通用取数，不含任何控制、下发或写操作。
 * 仅用于 /group/energy-map-screen?project= 入口，不改变独立路由 /group/nandu-energy-screen 的演示行为。
 */
import request from '@/utils/request'
import { fetchDevicePages } from '../device-pages'

const HEADERS = { userDeviceType: 'PC', 'Content-Type': 'application/json' }
const CONTENT = '/api/ebc-content'
const SETTLE = '/api/ebc-settle'
const NANDU_STATION_ID = '1299184320439459357'

function post (url, data, extra = {}) {
  return request({
    url,
    method: 'post',
    headers: HEADERS,
    data,
    skipErrorMessage: true,
    timeout: 60000,
    ...extra
  })
}

function success (response, label) {
  if (!response || String(response.code) !== '200' || response.data == null) {
    throw new Error((response && (response.msg || response.error)) || `${label}请求失败`)
  }
  return response.data
}

function numberOrNull (value) {
  if (value == null || value === '' || typeof value === 'boolean') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function sumNumber (rows, key) {
  return (Array.isArray(rows) ? rows : []).reduce((total, row) => total + (numberOrNull(row && row[key]) || 0), 0)
}

function ymd (date) {
  const pad = value => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function shiftDays (date, days) {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  next.setDate(next.getDate() + days)
  return next
}

export function buildProjectRange (now = new Date()) {
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
  const trendEnd = shiftDays(now, -1)
  const trendStart = shiftDays(trendEnd, -6)
  return {
    monthStart: ymd(monthStart),
    today: ymd(now),
    trendStart: ymd(trendStart),
    trendEnd: ymd(trendEnd),
    monthStartTime: `${ymd(monthStart)} 00:00:00`,
    todayEndTime: `${ymd(now)} 23:59:59`,
    todayStartTime: `${ymd(now)} 00:00:00`
  }
}

/** 本月四类用电：普通租户 / 租户空调 / 普通公区 / 公区空调。 */
export async function queryProjectEnergy (managerId, range) {
  const data = success(await post(`${CONTENT}/energyScreen/getShareEnergy`, {
    managerId: Number(managerId),
    firstPermissionMaId: Number(managerId),
    startTime: range.monthStartTime,
    endTime: range.todayEndTime
  }), '本月用电')
  const tenantGeneral = numberOrNull(data.tenantValue)
  const tenantAir = numberOrNull(data.tenantAirValue)
  const commonGeneral = numberOrNull(data.publicValue)
  const commonAir = numberOrNull(data.publicAirValue)
  if ([tenantGeneral, tenantAir, commonGeneral, commonAir].some(value => value == null)) {
    throw new Error('本月用电缺少四类拆分字段')
  }
  return { tenantGeneral, tenantAir, commonGeneral, commonAir }
}

/** 今日租户/公区用电。 */
export async function queryTodayUse (managerId, tenantType) {
  const data = success(await post(`${CONTENT}/customerWorkbench/queryCwTenantTodayUse`, {
    managerId: Number(managerId),
    tenantType
  }), '今日用电')
  return numberOrNull(data.useElec) || 0
}

/** 近 7 日逐日用电（完整日）。 */
export async function queryDailyElectricity (managerId) {
  const data = success(await post(`${SETTLE}/capitalManage/elecStatisticsNew`, null, {
    params: { type: 2, useType: 1, managerId: Number(managerId) }
  }), '近7日用电')
  return (Array.isArray(data) ? data : []).map(row => ({
    date: String(row.date || '').slice(0, 10),
    value: numberOrNull(row.useElec) || 0
  })).filter(row => /^\d{4}-\d{2}-\d{2}$/.test(row.date))
}

/** 区间用水（租户 + 公区），分别返回。 */
export async function queryWater (managerId, startDate, endDate) {
  const values = await Promise.all([0, 1].map(tenantType => post(`${CONTENT}/useReport/queryTenantWaterUseStatis`, {
    managerId: Number(managerId),
    startDate,
    endDate,
    dataType: 2,
    useType: 0,
    tenantType
  }).then(response => success(response, '用水统计'))))
  const tenant = sumNumber(values[0], 'waterUse')
  const common = sumNumber(values[1], 'waterUse')
  return { tenant, public: common, total: tenant + common }
}

/** 电表 / 水表 / 空调内机总量与离线量。 */
export async function queryDeviceStatistics (managerId, date) {
  const data = success(await post(`${CONTENT}/energyScreen/getDeviceTopStatistics`, {
    managerId: Number(managerId),
    firstPermissionMaId: Number(managerId),
    startTime: `${date} 00:00:00`,
    endTime: `${date} 23:59:59`
  }), '设备统计')
  const metric = (totalKey, offlineKey) => {
    const total = numberOrNull(data[totalKey])
    const offline = numberOrNull(data[offlineKey])
    if (total == null || offline == null) throw new Error(`设备统计缺少字段：${totalKey}`)
    return { total: Math.max(0, total), offline: Math.max(0, Math.min(total, offline)) }
  }
  return {
    electric: metric('meterCount', 'meterOfflineCount'),
    water: metric('waterCount', 'waterOfflineCount'),
    air: metric('airCount', 'airOfflineCount')
  }
}

/** 本月账单与实收（预付费口径，payType=0）。 */
export async function queryBills (managerId, range) {
  const data = success(await post(`${SETTLE}/capitalManage/tenantSettleStatis`, {
    managerId: Number(managerId),
    tenantId: '',
    startDate: range.monthStart,
    endDate: range.today,
    tenantStatus: '0',
    tenantType: 0,
    payType: 0
  }), '本月账单')
  const bill = numberOrNull(data.billTotalAmt)
  const pay = numberOrNull(data.payTotalAmt)
  const elecBill = numberOrNull(data.elecBillTotalAmt) || 0
  const waterBill = numberOrNull(data.waterBillTotalAmt) || 0
  const elecPay = numberOrNull(data.elecPayTotalAmt) || 0
  const waterPay = numberOrNull(data.waterPayTotalAmt) || 0
  if (bill == null || pay == null) throw new Error('本月账单缺少应收或实收字段')
  return { bill, pay, elecBill, waterBill, elecPay, waterPay }
}

/** 租户账户留存余额。 */
export async function queryRetain (managerId) {
  const response = await post(`${SETTLE}/capitalManage/tenantRetainStatis`, null, { params: { managerId: Number(managerId) } })
  const data = success(response, '留存金额')
  return numberOrNull(data.totalRetainAmt) || 0
}

async function queryDeviceList (url, managerId, label) {
  const result = await fetchDevicePages(async page => success(await post(url, {
    managerId: Number(managerId), page
  }), label))
  if (result.truncated) throw new Error(`${label}列表未获取完整，暂不参与全量统计`)
  return result.rows
}

/** 空调设备列表（只读）。 */
export async function queryAirDevices (managerId) {
  return queryDeviceList(`${CONTENT}/airInfo/pageAir`, managerId, '空调设备')
}

/** 电表列表（只读）。 */
export async function queryMeters (managerId) {
  return queryDeviceList(`${CONTENT}/meter/pageMeterInfo`, managerId, '电表列表')
}

/** 水表列表（只读）。 */
export async function queryWaterMeters (managerId) {
  return queryDeviceList(`${CONTENT}/water/pageWater`, managerId, '水表列表')
}

// ── 光伏（仅南都电站；只读）─────────────────────────────
function pvData (payload) {
  let data = payload
  for (let i = 0; i < 4; i += 1) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return data
    if (data.success === false || (data.code != null && !['0', '200'].includes(String(data.code)))) throw new Error('光伏服务返回异常')
    if (data.data == null) break
    data = data.data
  }
  return data
}

/** 本月光伏发电 / 自用 / 上网。 */
export async function querySolarMonth (month) {
  const base = process.env.NODE_ENV === 'development' ? '/api/asset-command-pv/' : 'https://grid.geekpartytech.com/api/station/'
  const response = await fetch(base + 'month', {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stationId: NANDU_STATION_ID, timeZone: 8, month })
  })
  if (!response.ok) throw new Error(`光伏服务请求失败：HTTP ${response.status}`)
  const data = pvData(await response.json())
  const records = Array.isArray(data) ? data : (data && (data.records || data.list))
  if (!Array.isArray(records) || !records.length) throw new Error('本月光伏暂无记录')
  const amount = (row, key, optional = false) => {
    const raw = numberOrNull(row[key])
    if (raw == null) {
      if (optional) return null
      throw new Error('光伏统计缺少电量字段')
    }
    const unit = String(row.energyStr || 'MWh').toLowerCase()
    const factor = { mwh: 1000, kwh: 1, wh: 0.001 }[unit]
    if (!factor) throw new Error('不支持的光伏电量单位')
    const pec = numberOrNull(row.energyPec)
    return raw * (pec == null ? 0.001 : pec) * factor
  }
  const generated = records.reduce((total, row) => total + amount(row, 'energy'), 0)
  const selfValues = records.map(row => amount(row, 'oneSelf', true))
  const exportValues = records.map(row => amount(row, 'gridSellEnergy', true))
  const self = selfValues.some(value => value == null) ? null : selfValues.reduce((a, b) => a + b, 0)
  const exported = exportValues.some(value => value == null) ? null : exportValues.reduce((a, b) => a + b, 0)
  const valid = self != null && exported != null && Math.abs(generated - self - exported) < Math.max(0.1, generated * 0.001)
  return { generated, self: valid ? self : null, exported: valid ? exported : null }
}
